// Chart configuration constants and utilities for SummaryCharts
import moment from "moment";

// Chart type definitions
export const CHART_TYPES = {
  USERS: "users",
  SESSIONS: "sessions",
  VIDEOS: "videos",
  ISSUES: "issues",
  ANALYSIS: "analysis",
  ISSUES_BY_PERCENTAGE: "issuesByPercentage",
  ANALYSIS_BY_PERCENTAGE: "analysisByPercentage",
  SCREEN_RECORDINGS: "screenRecordings",
};

// Base chart options
export const createBaseChartOptions = (title) => ({
  responsive: true,
  plugins: {
    legend: {
      position: "top",
    },
    title: {
      display: true,
      text: title,
      fontSize: "20",
    },
  },
  scales: {
    yAxes: [
      {
        ticks: {
          beginAtZero: true,
        },
      },
    ],
  },
});

// Chart colors
export const CHART_COLORS = {
  ACCUMULATIVE: "rgb(155, 99, 132, 0.5)",
  ACCUMULATIVE_BORDER: "rgb(155, 99, 132)",
  TOTAL: "rgb(255, 99, 132, 0.5)",
  TOTAL_BORDER: "rgb(255, 99, 132)",
  COACHES: "rgb(70, 185, 89, 0.5)",
  COACHES_BORDER: "rgb(70, 185, 89)",
  STUDENTS: "rgb(41, 121, 255, 0.5)",
  STUDENTS_BORDER: "rgb(41, 121, 255)",
  FREE_STUDENTS: "rgb(255, 255, 0, 0.5)",
  FREE_STUDENTS_BORDER: "rgb(255, 255, 0)",
  PAID_STUDENTS: "rgb(0, 0, 128, 0.5)",
  PAID_STUDENTS_BORDER: "rgb(0, 0, 128)",
  FON: "rgb(255, 128, 0, 0.5)",
  FON_BORDER: "rgb(255, 128, 0)",
  DTL: "rgb(255, 255, 0, 0.5)",
  DTL_BORDER: "rgb(255, 255, 0)",
  ANALYSIS_FON: "rgb(0, 255, 255, 0.5)",
  ANALYSIS_FON_BORDER: "rgb(0, 255, 255)",
  ANALYSIS_DTL: "rgb(255, 0, 255, 0.5)",
  ANALYSIS_DTL_BORDER: "rgb(255, 0, 255)",
  PERCENTAGE: "rgb(52, 170, 235, 0.5)",
  PERCENTAGE_BORDER: "rgb(52, 160, 235)",
};

// Chart data processors
export const processUsersData = (userData) => {
  if (!userData || userData.length === 0) return { labels: [], datasets: [] };

  const dates = userData.map((item) => item.date);
  const totalUsers = userData.map((item) => item.total || 0);

  // Calculate accumulative totals
  const overall = [];
  for (let i = 0; i < totalUsers.length; i++) {
    overall[i] = totalUsers.slice(0, i + 1).reduce((sum, val) => sum + val, 0);
  }

  return {
    labels: dates,
    datasets: [
      {
        label: "Accumulative",
        backgroundColor: CHART_COLORS.ACCUMULATIVE,
        borderColor: CHART_COLORS.ACCUMULATIVE_BORDER,
        data: overall,
        hidden: true,
      },
      {
        label: "Total",
        backgroundColor: CHART_COLORS.TOTAL,
        borderColor: CHART_COLORS.TOTAL_BORDER,
        data: userData.map((item) => item.total || 0),
      },
      {
        label: "Coaches",
        backgroundColor: CHART_COLORS.COACHES,
        borderColor: CHART_COLORS.COACHES_BORDER,
        data: userData.map((item) => item.coaches || 0),
      },
      {
        label: "Students",
        backgroundColor: CHART_COLORS.STUDENTS,
        borderColor: CHART_COLORS.STUDENTS_BORDER,
        data: userData.map((item) => item.students || 0),
      },
      {
        label: "Free Plan Students",
        backgroundColor: CHART_COLORS.FREE_STUDENTS,
        borderColor: CHART_COLORS.FREE_STUDENTS_BORDER,
        data: userData.map((item) => item.freeStudents || 0),
      },
      {
        label: "Paid Plan Students",
        backgroundColor: CHART_COLORS.PAID_STUDENTS,
        borderColor: CHART_COLORS.PAID_STUDENTS_BORDER,
        data: userData.map((item) => item.paidStudents || 0),
      },
    ],
  };
};

export const processSessionsData = (sessionData) => {
  if (!sessionData || sessionData.length === 0)
    return { labels: [], datasets: [] };

  const dates = sessionData.map((item) => item.date);
  const totalSessions = sessionData.map((item) => item.total || 0);

  const overall = [];
  for (let i = 0; i < totalSessions.length; i++) {
    overall[i] = totalSessions
      .slice(0, i + 1)
      .reduce((sum, val) => sum + val, 0);
  }

  return {
    labels: dates,
    datasets: [
      {
        label: "Accumulative",
        backgroundColor: CHART_COLORS.ACCUMULATIVE,
        borderColor: CHART_COLORS.ACCUMULATIVE_BORDER,
        data: overall,
        hidden: true,
      },
      {
        label: "Total",
        backgroundColor: CHART_COLORS.TOTAL,
        borderColor: CHART_COLORS.TOTAL_BORDER,
        data: totalSessions,
      },
    ],
  };
};

export const processVideosData = (videoData) => {
  if (!videoData || videoData.length === 0) return { labels: [], datasets: [] };

  const dates = videoData.map((item) => item.date);
  const totalVideos = videoData.map((item) => item.total || 0);

  const overall = [];
  for (let i = 0; i < totalVideos.length; i++) {
    overall[i] = totalVideos.slice(0, i + 1).reduce((sum, val) => sum + val, 0);
  }

  return {
    labels: dates,
    datasets: [
      {
        label: "Accumulative",
        backgroundColor: CHART_COLORS.ACCUMULATIVE,
        borderColor: CHART_COLORS.ACCUMULATIVE_BORDER,
        data: overall,
        hidden: true,
      },
      {
        label: "Total",
        backgroundColor: CHART_COLORS.TOTAL,
        borderColor: CHART_COLORS.TOTAL_BORDER,
        data: totalVideos,
      },
      {
        label: "FON Total",
        backgroundColor: CHART_COLORS.FON,
        borderColor: CHART_COLORS.FON_BORDER,
        data: videoData.map((item) => item.fon || 0),
      },
      {
        label: "DTL Total",
        backgroundColor: CHART_COLORS.DTL,
        borderColor: CHART_COLORS.DTL_BORDER,
        data: videoData.map((item) => item.dtl || 0),
      },
    ],
  };
};

export const processIssuesData = (issuesData, analysisData) => {
  if (
    (!issuesData || issuesData.length === 0) &&
    (!analysisData || analysisData.length === 0)
  ) {
    return { labels: [], datasets: [] };
  }

  const dates = issuesData.map((item) => item.date);
  const totalIssues = issuesData.map((item) => item.total || 0);
  const totalAnalysis = analysisData.map((item) => item.total || 0);

  const issuesVSanalysis = [];
  const issuesOverall = [];
  const analysisOverall = [];

  for (let i = 0; i < totalIssues.length; i++) {
    const issueCount = totalIssues[i] || 0;
    const analysisCount = totalAnalysis[i] || 0;
    const total = issueCount + analysisCount;
    issuesVSanalysis[i] =
      total > 0 ? ((issueCount / total) * 100).toFixed(0) : 0;
    issuesOverall[i] = totalIssues
      .slice(0, i + 1)
      .reduce((sum, val) => sum + val, 0);
    analysisOverall[i] = totalAnalysis
      .slice(0, i + 1)
      .reduce((sum, val) => sum + val, 0);
  }

  return {
    labels: dates,
    datasets: [
      {
        label: "S&M Accumulative",
        backgroundColor: CHART_COLORS.ACCUMULATIVE,
        borderColor: CHART_COLORS.ACCUMULATIVE_BORDER,
        data: issuesOverall,
        hidden: true,
      },
      {
        label: "Analysis Accumulative",
        backgroundColor: CHART_COLORS.COACHES,
        borderColor: "rgb(36, 61, 40)",
        data: analysisOverall,
        hidden: true,
      },
      {
        label: "S&M Total",
        backgroundColor: CHART_COLORS.TOTAL,
        borderColor: CHART_COLORS.TOTAL_BORDER,
        data: totalIssues,
      },
      {
        label: "Analysis Total",
        backgroundColor: CHART_COLORS.COACHES,
        borderColor: CHART_COLORS.COACHES_BORDER,
        data: totalAnalysis,
      },
      {
        label: "S&M FON Total",
        backgroundColor: CHART_COLORS.FON,
        borderColor: CHART_COLORS.FON_BORDER,
        data: issuesData.map((item) => item.fon || 0),
      },
      {
        label: "S&M DTL Total",
        backgroundColor: CHART_COLORS.DTL,
        borderColor: CHART_COLORS.DTL_BORDER,
        data: issuesData.map((item) => item.dtl || 0),
      },
      {
        label: "Analysis FON Total",
        backgroundColor: CHART_COLORS.ANALYSIS_FON,
        borderColor: CHART_COLORS.ANALYSIS_FON_BORDER,
        data: analysisData.map((item) => item.fon || 0),
      },
      {
        label: "Analysis DTL Total",
        backgroundColor: CHART_COLORS.ANALYSIS_DTL,
        borderColor: CHART_COLORS.ANALYSIS_DTL_BORDER,
        data: analysisData.map((item) => item.dtl || 0),
      },
      {
        label: "S&M vs Analysis (%)",
        backgroundColor: CHART_COLORS.PERCENTAGE,
        borderColor: CHART_COLORS.PERCENTAGE_BORDER,
        data: issuesVSanalysis,
      },
    ],
  };
};

export const processAnalysisData = (analysisData, issuesData) => {
  if (
    (!analysisData || analysisData.length === 0) &&
    (!issuesData || issuesData.length === 0)
  ) {
    return { labels: [], datasets: [] };
  }

  const dates = analysisData.map((item) => item.date);
  const totalAnalysis = analysisData.map((item) => item.total || 0);
  const totalIssues = issuesData.map((item) => item.total || 0);

  const analysisVSissues = [];
  const issuesOverall = [];
  const analysisOverall = [];

  for (let i = 0; i < totalAnalysis.length; i++) {
    const analysisCount = totalAnalysis[i] || 0;
    const issueCount = totalIssues[i] || 0;
    const total = analysisCount + issueCount;
    analysisVSissues[i] =
      total > 0 ? ((analysisCount / total) * 100).toFixed(0) : 0;
    analysisOverall[i] = totalAnalysis
      .slice(0, i + 1)
      .reduce((sum, val) => sum + val, 0);
    issuesOverall[i] = totalIssues
      .slice(0, i + 1)
      .reduce((sum, val) => sum + val, 0);
  }

  return {
    labels: dates,
    datasets: [
      {
        label: "Analysis Accumulative",
        backgroundColor: CHART_COLORS.COACHES,
        borderColor: "rgb(36, 61, 40)",
        data: analysisOverall,
        hidden: true,
      },
      {
        label: "S&M Accumulative",
        backgroundColor: CHART_COLORS.ACCUMULATIVE,
        borderColor: CHART_COLORS.ACCUMULATIVE_BORDER,
        data: issuesOverall,
        hidden: true,
      },
      {
        label: "Analysis Total",
        backgroundColor: CHART_COLORS.TOTAL,
        borderColor: CHART_COLORS.TOTAL_BORDER,
        data: totalAnalysis,
      },
      {
        label: "S&M Total",
        backgroundColor: CHART_COLORS.COACHES,
        borderColor: CHART_COLORS.COACHES_BORDER,
        data: totalIssues,
      },
      {
        label: "Analysis FON Total",
        backgroundColor: CHART_COLORS.FON,
        borderColor: CHART_COLORS.FON_BORDER,
        data: analysisData.map((item) => item.fon || 0),
      },
      {
        label: "Analysis DTL Total",
        backgroundColor: CHART_COLORS.ANALYSIS_DTL,
        borderColor: CHART_COLORS.ANALYSIS_DTL_BORDER,
        data: analysisData.map((item) => item.dtl || 0),
      },
      {
        label: "S&M FON Total",
        backgroundColor: CHART_COLORS.DTL,
        borderColor: CHART_COLORS.DTL_BORDER,
        data: issuesData.map((item) => item.fon || 0),
      },
      {
        label: "S&M DTL Total",
        backgroundColor: CHART_COLORS.ANALYSIS_FON,
        borderColor: CHART_COLORS.ANALYSIS_FON_BORDER,
        data: issuesData.map((item) => item.dtl || 0),
      },
      {
        label: "Analysis vs S&M (%)",
        backgroundColor: CHART_COLORS.PERCENTAGE,
        borderColor: CHART_COLORS.PERCENTAGE_BORDER,
        data: analysisVSissues,
      },
    ],
  };
};

export const processIssuesPctData = (issuesPctData, analysisPctData) => {
  if (
    (!issuesPctData || issuesPctData.length === 0) &&
    (!analysisPctData || analysisPctData.length === 0)
  ) {
    return { labels: [], datasets: [] };
  }

  const dates = issuesPctData.map((item) => item.date);

  return {
    labels: dates,
    datasets: [
      {
        label: "S&M FON Total (%)",
        backgroundColor: CHART_COLORS.FON,
        borderColor: CHART_COLORS.FON_BORDER,
        data: issuesPctData.map((item) => item.fon || 0),
      },
      {
        label: "S&M DTL Total (%)",
        backgroundColor: CHART_COLORS.DTL,
        borderColor: CHART_COLORS.DTL_BORDER,
        data: issuesPctData.map((item) => item.dtl || 0),
      },
      {
        label: "Analysis FON Total (%)",
        backgroundColor: CHART_COLORS.ANALYSIS_FON,
        borderColor: CHART_COLORS.ANALYSIS_FON_BORDER,
        data: analysisPctData.map((item) => item.fon || 0),
      },
      {
        label: "Analysis DTL Total (%)",
        backgroundColor: CHART_COLORS.ANALYSIS_DTL,
        borderColor: CHART_COLORS.ANALYSIS_DTL_BORDER,
        data: analysisPctData.map((item) => item.dtl || 0),
      },
    ],
  };
};

export const processAnalysisPctData = (analysisPctData, issuesPctData) => {
  if (
    (!analysisPctData || analysisPctData.length === 0) &&
    (!issuesPctData || issuesPctData.length === 0)
  ) {
    return { labels: [], datasets: [] };
  }

  const dates = analysisPctData.map((item) => item.date);

  return {
    labels: dates,
    datasets: [
      {
        label: "Analysis FON Total (%)",
        backgroundColor: CHART_COLORS.FON,
        borderColor: CHART_COLORS.FON_BORDER,
        data: analysisPctData.map((item) => item.fon || 0),
      },
      {
        label: "Analysis DTL Total (%)",
        backgroundColor: CHART_COLORS.ANALYSIS_DTL,
        borderColor: CHART_COLORS.ANALYSIS_DTL_BORDER,
        data: analysisPctData.map((item) => item.dtl || 0),
      },
      {
        label: "S&M FON Total (%)",
        backgroundColor: CHART_COLORS.DTL,
        borderColor: CHART_COLORS.DTL_BORDER,
        data: issuesPctData.map((item) => item.fon || 0),
      },
      {
        label: "S&M DTL Total (%)",
        backgroundColor: CHART_COLORS.ANALYSIS_FON,
        borderColor: CHART_COLORS.ANALYSIS_FON_BORDER,
        data: issuesPctData.map((item) => item.dtl || 0),
      },
    ],
  };
};

export const processScreenRecordingsData = (screenData) => {
  if (!screenData || screenData.length === 0)
    return { labels: [], datasets: [] };

  const dates = screenData.map((item) => item.date);
  const totalRecordings = screenData.map((item) => item.total || 0);

  const overall = [];
  for (let i = 0; i < totalRecordings.length; i++) {
    overall[i] = totalRecordings
      .slice(0, i + 1)
      .reduce((sum, val) => sum + val, 0);
  }

  return {
    labels: dates,
    datasets: [
      {
        label: "Accumulative",
        backgroundColor: CHART_COLORS.ACCUMULATIVE,
        borderColor: CHART_COLORS.ACCUMULATIVE_BORDER,
        data: overall,
        hidden: true,
      },
      {
        label: "Total",
        backgroundColor: CHART_COLORS.TOTAL,
        borderColor: CHART_COLORS.TOTAL_BORDER,
        data: totalRecordings,
      },
    ],
  };
};

// Helper function to format date key based on period
const formatDateKey = (date, period) => {
  const momentDate = moment(date);
  switch (period) {
    case "byDays":
      return momentDate.format("YYYY-MM-DD");
    case "byWeeks":
      return `${momentDate.format("YYYY")}-${String(momentDate.week()).padStart(
        2,
        "0"
      )}`;
    case "byMonths":
      return momentDate.format("YYYY-MM");
    case "byYears":
      return momentDate.format("YYYY");
    default:
      return momentDate.format("YYYY-MM-DD");
  }
};

// Helper function to sum array
const sumArray = (arr, n) => {
  let total = 0;
  if (n <= arr?.length) {
    for (let i = 0; i < n; i++) {
      total += arr[i] || 0;
    }
  }
  return total;
};

// Process subscriptions data
export const processSubscriptionsData = (
  subscriptions,
  selectionRange,
  statPeriod
) => {
  if (!subscriptions || subscriptions.length === 0) {
    return { labels: [], datasets: [] };
  }

  const overall = [];
  const total = [];
  const elavon = [];
  const stripe = [];
  const apple = [];
  const android = [];
  const canceled = [];
  const studio = [];

  subscriptions.forEach((sub) => {
    if (sub.startDate) {
      const created = moment(sub.startDate * 1);
      if (
        created >= moment(selectionRange.startDate) &&
        created <= moment(selectionRange.endDate).endOf("day")
      ) {
        const key = formatDateKey(created, statPeriod);

        const totalIndex = total.findIndex((item) => item.key === key);
        if (totalIndex < 0) {
          total.push({ key: key, count: 1 });
          overall.push({ key: key, count: 1 });
        } else {
          total[totalIndex].count++;
          overall[totalIndex].count++;
        }

        // Elavon
        const elavonIndex = elavon.findIndex((item) => item.key === key);
        if (elavonIndex < 0) {
          elavon.push({
            key: key,
            count: sub.platform === "Elavon" ? 1 : 0,
          });
        } else if (sub.platform === "Elavon") {
          elavon[elavonIndex].count++;
        }

        // Stripe
        const stripeIndex = stripe.findIndex((item) => item.key === key);
        if (stripeIndex < 0) {
          stripe.push({
            key: key,
            count: sub.platform === "Stripe" ? 1 : 0,
          });
        } else if (sub.platform === "Stripe") {
          stripe[stripeIndex].count++;
        }

        // Apple
        const appleIndex = apple.findIndex((item) => item.key === key);
        if (appleIndex < 0) {
          apple.push({ key: key, count: sub.platform === "Apple" ? 1 : 0 });
        } else if (sub.platform === "Apple") {
          apple[appleIndex].count++;
        }

        // Android
        const androidIndex = android.findIndex((item) => item.key === key);
        if (androidIndex < 0) {
          android.push({
            key: key,
            count: sub.platform === "Android" ? 1 : 0,
          });
        } else if (sub.platform === "Android") {
          android[androidIndex].count++;
        }

        // Studio
        if (sub.productId?.includes("studio_master")) {
          const studioIndex = studio.findIndex((item) => item.key === key);
          if (studioIndex < 0) {
            studio.push({
              key: key,
              count: sub.platform === "Stripe" ? 1 : 0,
            });
          } else if (sub.platform === "Stripe") {
            studio[studioIndex].count++;
          }
        }

        // Canceled
        if (
          !sub.productId?.includes("enterprise_master") ||
          !sub.productId?.includes("studio_master") ||
          (sub.productId?.includes("enterprise_master") &&
            ![0, 100, 200].includes(parseInt(sub.amount)))
        ) {
          const canceledIndex = canceled.findIndex((item) => item.key === key);
          if (canceledIndex < 0) {
            canceled.push({
              key: key,
              count: !sub.autoRenewal && sub.transactionId !== "" ? 1 : 0,
            });
          } else if (!sub.autoRenewal && sub.transactionId !== "") {
            canceled[canceledIndex].count++;
          }
        }
      }
    }
  });

  // Sort all arrays
  [overall, total, elavon, stripe, apple, android, canceled, studio].forEach(
    (arr) => arr.sort((a, b) => a.key.localeCompare(b.key))
  );

  // Calculate accumulative
  if (overall.length > 0) {
    const totalCounts = total.map((item) => item.count);
    for (let i = 0; i < overall.length; i++) {
      overall[i].count = sumArray(totalCounts, i + 1);
    }
  }

  return {
    labels: total.map((item) => item.key),
    datasets: [
      {
        label: "Accumulative",
        backgroundColor: CHART_COLORS.ACCUMULATIVE,
        borderColor: CHART_COLORS.ACCUMULATIVE_BORDER,
        data: overall.map((item) => item.count),
        hidden: true,
      },
      {
        label: "Total",
        backgroundColor: CHART_COLORS.TOTAL,
        borderColor: CHART_COLORS.TOTAL_BORDER,
        data: total.map((item) => item.count),
      },
      {
        label: "Elavon",
        backgroundColor: CHART_COLORS.COACHES,
        borderColor: CHART_COLORS.COACHES_BORDER,
        data: elavon.map((item) => item.count),
      },
      {
        label: "Stripe",
        backgroundColor: "rgb(255, 153, 51, 0.5)",
        borderColor: "rgb(255, 153, 51)",
        data: stripe.map((item) => item.count),
      },
      {
        label: "Apple",
        backgroundColor: CHART_COLORS.STUDENTS,
        borderColor: CHART_COLORS.STUDENTS_BORDER,
        data: apple.map((item) => item.count),
      },
      {
        label: "Android",
        backgroundColor: "rgb(75, 192, 192, 0.5)",
        borderColor: "rgb(75, 192, 192)",
        data: android.map((item) => item.count),
      },
      {
        label: "Studio",
        backgroundColor: "rgb(0, 0, 0, 0.5)",
        borderColor: "rgb(0, 0, 0)",
        data: studio.map((item) => item.count),
      },
      {
        label: "Canceled",
        backgroundColor: "rgb(255, 240, 0, 0.5)",
        borderColor: "rgb(255, 240, 0)",
        data: canceled.map((item) => item.count),
      },
    ],
  };
};

// Process recurring subscriptions data
export const processRecurringSubscriptionsData = (
  subscriptions,
  selectionRange,
  statPeriod
) => {
  if (!subscriptions || subscriptions.length === 0) {
    return { labels: [], datasets: [] };
  }

  const overall = [];
  const total = [];
  const elavon = [];
  const stripe = [];
  const apple = [];
  const android = [];
  const staff = [];

  subscriptions.forEach((sub) => {
    if (
      sub.startDate &&
      (sub.autoRenewal ||
        (sub.productId?.includes("enterprise_master") && !sub.autoRenewal))
    ) {
      const created = moment(sub.startDate * 1);
      if (
        created >= moment(selectionRange.startDate) &&
        created <= moment(selectionRange.endDate).endOf("day")
      ) {
        const key = formatDateKey(created, statPeriod);

        if (
          sub.autoRenewal ||
          parseInt(sub.amount) === 0 ||
          (sub.transactionId !== "" &&
            (parseInt(sub.amount) === 100 || parseInt(sub.amount) === 200))
        ) {
          const totalIndex = total.findIndex((item) => item.key === key);
          if (totalIndex < 0) {
            total.push({ key: key, count: 1 });
            overall.push({ key: key, count: 1 });
          } else {
            total[totalIndex].count++;
            overall[totalIndex].count++;
          }
        }

        if (sub.autoRenewal) {
          // Elavon
          const elavonIndex = elavon.findIndex((item) => item.key === key);
          if (elavonIndex < 0) {
            elavon.push({
              key: key,
              count: sub.platform === "Elavon" ? 1 : 0,
            });
          } else if (sub.platform === "Elavon") {
            elavon[elavonIndex].count++;
          }

          // Stripe
          const stripeIndex = stripe.findIndex((item) => item.key === key);
          if (stripeIndex < 0) {
            stripe.push({
              key: key,
              count: sub.platform === "Stripe" ? 1 : 0,
            });
          } else if (sub.platform === "Stripe") {
            stripe[stripeIndex].count++;
          }
        }

        // Apple
        const appleIndex = apple.findIndex((item) => item.key === key);
        if (appleIndex < 0) {
          apple.push({ key: key, count: sub.platform === "Apple" ? 1 : 0 });
        } else if (sub.platform === "Apple") {
          apple[appleIndex].count++;
        }

        // Android
        const androidIndex = android.findIndex((item) => item.key === key);
        if (androidIndex < 0) {
          android.push({
            key: key,
            count: sub.platform === "Android" ? 1 : 0,
          });
        } else if (sub.platform === "Android") {
          android[androidIndex].count++;
        }

        // Staff
        const staffIndex = staff.findIndex((item) => item.key === key);
        if (staffIndex < 0) {
          staff.push({
            key: key,
            count:
              sub.productId?.includes("enterprise_master") &&
              !sub.autoRenewal &&
              (parseInt(sub.amount) === 0 ||
                (sub.transactionId !== "" &&
                  (parseInt(sub.amount) === 100 ||
                    parseInt(sub.amount) === 200)))
                ? 1
                : 0,
          });
        } else if (
          sub.productId?.includes("enterprise_master") &&
          !sub.autoRenewal &&
          (parseInt(sub.amount) === 0 ||
            (sub.transactionId !== "" &&
              (parseInt(sub.amount) === 100 || parseInt(sub.amount) === 200)))
        ) {
          staff[staffIndex].count++;
        }
      }
    }
  });

  // Sort all arrays
  [overall, total, elavon, stripe, apple, android, staff].forEach((arr) =>
    arr.sort((a, b) => a.key.localeCompare(b.key))
  );

  // Calculate accumulative
  if (overall.length > 0) {
    const totalCounts = total.map((item) => item.count);
    for (let i = 0; i < overall.length; i++) {
      overall[i].count = sumArray(totalCounts, i + 1);
    }
  }

  return {
    labels: total.map((item) => item.key),
    datasets: [
      {
        label: "Accumulative",
        backgroundColor: CHART_COLORS.ACCUMULATIVE,
        borderColor: CHART_COLORS.ACCUMULATIVE_BORDER,
        data: overall.map((item) => item.count),
        hidden: true,
      },
      {
        label: "Total",
        backgroundColor: CHART_COLORS.TOTAL,
        borderColor: CHART_COLORS.TOTAL_BORDER,
        data: total.map((item) => item.count),
      },
      {
        label: "Elavon",
        backgroundColor: CHART_COLORS.COACHES,
        borderColor: CHART_COLORS.COACHES_BORDER,
        data: elavon.map((item) => item.count),
      },
      {
        label: "Stripe",
        backgroundColor: "rgb(255, 153, 51, 0.5)",
        borderColor: "rgb(255, 153, 51)",
        data: stripe.map((item) => item.count),
      },
      {
        label: "Apple",
        backgroundColor: CHART_COLORS.STUDENTS,
        borderColor: CHART_COLORS.STUDENTS_BORDER,
        data: apple.map((item) => item.count),
      },
      {
        label: "Android",
        backgroundColor: "rgb(75, 192, 192, 0.5)",
        borderColor: "rgb(75, 192, 192)",
        data: android.map((item) => item.count),
      },
      {
        label: "Staff",
        backgroundColor: "rgb(175, 192, 192, 0.5)",
        borderColor: "rgb(175, 192, 192)",
        data: staff.map((item) => item.count),
      },
    ],
  };
};

// Process canceled subscriptions data
export const processCanceledSubscriptionsData = (
  canceledSubscriptions,
  selectionRange,
  statPeriod
) => {
  if (!canceledSubscriptions || canceledSubscriptions.length === 0) {
    return { labels: [], datasets: [] };
  }

  const overall = [];
  const total = [];
  const elavon = [];
  const stripe = [];
  const apple = [];
  const android = [];

  canceledSubscriptions.forEach((sub) => {
    if (sub.startDate && !sub.autoRenewal && sub.transactionId !== "") {
      const created = moment(sub.startDate * 1);
      if (
        created >= moment(selectionRange.startDate) &&
        created <= moment(selectionRange.endDate).endOf("day")
      ) {
        const key = formatDateKey(created, statPeriod);

        if (
          !sub.productId?.includes("enterprise_master") ||
          (sub.productId?.includes("enterprise_master") &&
            ![0, 100, 200].includes(parseInt(sub.amount)))
        ) {
          const totalIndex = total.findIndex((item) => item.key === key);
          if (totalIndex < 0) {
            total.push({ key: key, count: 1 });
            overall.push({ key: key, count: 1 });
          } else {
            total[totalIndex].count++;
            overall[totalIndex].count++;
          }

          // Elavon
          const elavonIndex = elavon.findIndex((item) => item.key === key);
          if (elavonIndex < 0) {
            elavon.push({
              key: key,
              count: sub.platform === "Elavon" ? 1 : 0,
            });
          } else if (sub.platform === "Elavon") {
            elavon[elavonIndex].count++;
          }

          // Stripe
          const stripeIndex = stripe.findIndex((item) => item.key === key);
          if (stripeIndex < 0) {
            stripe.push({
              key: key,
              count: sub.platform === "Stripe" ? 1 : 0,
            });
          } else if (sub.platform === "Stripe") {
            stripe[stripeIndex].count++;
          }

          // Apple
          const appleIndex = apple.findIndex((item) => item.key === key);
          if (appleIndex < 0) {
            apple.push({
              key: key,
              count: sub.platform === "Apple" ? 1 : 0,
            });
          } else if (sub.platform === "Apple") {
            apple[appleIndex].count++;
          }

          // Android
          const androidIndex = android.findIndex((item) => item.key === key);
          if (androidIndex < 0) {
            android.push({
              key: key,
              count: sub.platform === "Android" ? 1 : 0,
            });
          } else if (sub.platform === "Android") {
            android[androidIndex].count++;
          }
        }
      }
    }
  });

  // Sort all arrays
  [overall, total, elavon, stripe, apple, android].forEach((arr) =>
    arr.sort((a, b) => a.key.localeCompare(b.key))
  );

  // Calculate accumulative
  if (overall.length > 0) {
    const totalCounts = total.map((item) => item.count);
    for (let i = 0; i < overall.length; i++) {
      overall[i].count = sumArray(totalCounts, i + 1);
    }
  }

  return {
    labels: total.map((item) => item.key),
    datasets: [
      {
        label: "Accumulative",
        backgroundColor: CHART_COLORS.ACCUMULATIVE,
        borderColor: CHART_COLORS.ACCUMULATIVE_BORDER,
        data: overall.map((item) => item.count),
        hidden: true,
      },
      {
        label: "Total",
        backgroundColor: CHART_COLORS.TOTAL,
        borderColor: CHART_COLORS.TOTAL_BORDER,
        data: total.map((item) => item.count),
      },
      {
        label: "Elavon",
        backgroundColor: CHART_COLORS.COACHES,
        borderColor: CHART_COLORS.COACHES_BORDER,
        data: elavon.map((item) => item.count),
      },
      {
        label: "Stripe",
        backgroundColor: "rgb(255, 153, 51, 0.5)",
        borderColor: "rgb(255, 153, 51)",
        data: stripe.map((item) => item.count),
      },
      {
        label: "Apple",
        backgroundColor: CHART_COLORS.STUDENTS,
        borderColor: CHART_COLORS.STUDENTS_BORDER,
        data: apple.map((item) => item.count),
      },
      {
        label: "Android",
        backgroundColor: "rgb(75, 192, 192, 0.5)",
        borderColor: "rgb(75, 192, 192)",
        data: android.map((item) => item.count),
      },
    ],
  };
};

// Chart titles
export const CHART_TITLES = {
  [CHART_TYPES.USERS]: "Users",
  [CHART_TYPES.SESSIONS]: "Sessions",
  [CHART_TYPES.VIDEOS]: "Videos",
  [CHART_TYPES.ISSUES]: "Swing And A Miss",
  [CHART_TYPES.ANALYSIS]: "Analysis",
  [CHART_TYPES.ISSUES_BY_PERCENTAGE]: "Swing And A Miss by %",
  [CHART_TYPES.ANALYSIS_BY_PERCENTAGE]: "Analysis by %",
  [CHART_TYPES.SCREEN_RECORDINGS]: "Screen Recordings",
  subscriptions: "All Subscriptions",
  recurringSubscriptions: "Recurring Subscriptions",
  canceledSubscriptions: "Canceled Subscriptions",
  confidenceScore: "Confidence Score",
  confidenceScoreByPercentage: "Confidence Score",
};
